import React, { useState } from "react";
import {
  Box,
  HStack,
  Button,
  Select,
  VStack,
  Text,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import { Chart, registerables, ChartOptions } from "chart.js";
import { format, getDaysInMonth, parseISO, getYear, getMonth } from "date-fns";
import { m } from "framer-motion";
import CustomDropdown from "../../../components/CustomDropdown";

// Register Chart.js plugins
Chart.register(...registerables);

// 🟡 Timesheet Entry Type
interface TimesheetEntry {
  date: string;
  hours: string; // Hours as string
  status: string;
}

interface TimesheetGraphProps {
  timesheets: TimesheetEntry[];
}

const TimesheetGraph: React.FC<TimesheetGraphProps> = ({ timesheets }) => {
  const [view, setView] = useState<"week" | "month" | "year" | "overall">(
    "month"
  );
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );

  // Dynamic theme colors (gold + black/white)
  const axisLabelColor = useColorModeValue("#374151", "#E5E7EB");
  const gridColor = useColorModeValue("rgba(107,114,128,0.2)", "rgba(75,85,99,0.35)");
  const textColor = useColorModeValue("#111827", "#FFFFFF");
  const goldBorder = "#B59143"; // matches zs.dark.goldTo
  const goldFill = "rgba(251,210,130,0.20)"; // based on zs.dark.goldFrom with alpha
  const goldPoint = "#B59143";
  const outlineHoverBg = useColorModeValue("rgba(181,145,67,0.08)", "rgba(251,210,130,0.10)");
  const isDarkDropdown = useColorModeValue(false, true);

  // 🟢 Filter Approved Entries for Selected Year and Selected Month
  const approvedEntries = timesheets.filter(
    (entry) =>
      (entry.status.toLowerCase() === "approved" || entry.status.toLowerCase() === "approve") &&
      parseISO(entry.date).getFullYear() === selectedYear &&
      (view !== "week" && view !== "month"
        ? true
        : getMonth(parseISO(entry.date)) + 1 === selectedMonth)
  );

  /**
   * 🧮 Utility: Parse Hours (String to Number) and Cap
   */
  const parseHours = (hours: string) => {
    const value = parseFloat(hours);
    return isNaN(value) ? 0 : Math.min(value, 24); // Cap at 24 hours/day
  };

  /**
   * 📊 Function to Group Data by Weeks, Months, or Years
   */
  const getFilteredEntries = () => {
    const grouped: Record<string, number> = {};
    let totalDaysWorked = 0;
    let totalHoursWorked = 0;

    // Get current date info for limiting display
    const now = new Date();
    const currentDay = now.getDate();
    const currentWeek = Math.ceil(currentDay / 7);
    const currentMonthIndex = now.getMonth(); // 0-based
    const currentYearValue = now.getFullYear();

    const daysInMonth = getDaysInMonth(
      new Date(selectedYear, selectedMonth - 1)
    );
    let labels: string[] = [];

    if (view === "week") {
      // 📅 Week View: Group Hours by 7-day weeks - Always show all 4 weeks
      labels = ["Week 1", "Week 2", "Week 3", "Week 4"];
      approvedEntries.forEach((entry) => {
        const date = parseISO(entry.date);
        const dayOfMonth = date.getDate();
        const weekNumber = Math.ceil(dayOfMonth / 7);
        const label = `Week ${weekNumber}`;
        if (!grouped[label]) grouped[label] = 0;
        grouped[label] += parseHours(entry.hours);
        totalHoursWorked += parseHours(entry.hours);
      });

      totalDaysWorked = new Set(
        approvedEntries.map(
          (entry) => parseISO(entry.date).toISOString().split("T")[0]
        )
      ).size;
    }

    if (view === "month") {
      // 📅 Month View: Group Hours by Days - Always show all days
      labels = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`);
      approvedEntries.forEach((entry) => {
        const date = parseISO(entry.date);
        const day = date.getDate().toString();
        if (!grouped[day]) grouped[day] = 0;
        grouped[day] += parseHours(entry.hours);
        totalHoursWorked += parseHours(entry.hours);
      });

      totalDaysWorked = new Set(
        approvedEntries.map(
          (entry) => parseISO(entry.date).toISOString().split("T")[0]
        )
      ).size;
    }

    if (view === "year") {
      // 📅 Year View: Group Days Worked by Months - Always show all 12 months
      labels = Array.from({ length: 12 }, (_, i) =>
        format(new Date(0, i), "MMM")
      );
      approvedEntries.forEach((entry) => {
        const date = parseISO(entry.date);
        const month = getMonth(date);
        const label = format(new Date(0, month), "MMM");
        if (!grouped[label]) grouped[label] = 0;
        grouped[label] += 1; // Count Days Worked
      });
      totalDaysWorked = approvedEntries.length;
    }

    if (view === "overall") {
      // 🌎 Overall View: Group Days Worked by Year
      const years = Array.from(
        new Set(timesheets.map((entry) => getYear(parseISO(entry.date))))
      ).sort();
      labels = years.map(String);
      timesheets.forEach((entry) => {
        const year = getYear(parseISO(entry.date)).toString();
        if (!grouped[year]) grouped[year] = 0;
        grouped[year] += 1; // Count Days Worked
      });
      totalDaysWorked = timesheets.length;
    }

    // Create values array with null for future periods to stop graph line
    const values = labels.map((label, index) => {
      const shouldShowData = (() => {
        if (view === "month") {
          const isCurrentMonth = selectedYear === currentYearValue && selectedMonth === (currentMonthIndex + 1);
          return !isCurrentMonth || (index + 1) <= currentDay;
        } else if (view === "week") {
          const isCurrentMonth = selectedYear === currentYearValue && selectedMonth === (currentMonthIndex + 1);
          return !isCurrentMonth || (index + 1) <= currentWeek;
        } else if (view === "year") {
          const isCurrentYear = selectedYear === currentYearValue;
          return !isCurrentYear || index <= currentMonthIndex;
        } else {
          // overall view
          return parseInt(label) <= currentYearValue;
        }
      })();

      return shouldShowData ? (grouped[label] || 0) : null;
    });

    return {
      labels,
      values,
      totalDaysWorked,
      totalHoursWorked,
    };
  };

  const filteredData = getFilteredEntries();

  // 🟣 Chart Data Configuration
  const chartData = {
    labels: filteredData.labels,
    datasets: [
      {
        label:
          view === "year"
            ? `Days Worked by Month (${selectedYear})`
            : view === "overall"
              ? "Days Worked by Year"
              : view === "month"
                ? `Hours Worked Each Day (${selectedMonth}/${selectedYear})`
                : `Hours Worked by Week (${selectedMonth}/${selectedYear})`,
        data: filteredData.values,
        borderColor: goldBorder,
        backgroundColor: goldFill,
        borderWidth: 1, // Made thinner from 2
        pointBackgroundColor: goldPoint,
        pointRadius: 1.5, // Made smaller from 4
        pointHoverRadius: 3, // Made smaller from 6
        tension: 0.2, // Slight curve for better readability
        fill: true, // Enable fill under the line
      },
    ],
  };

  // 🛠️ Chart.js Options
  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: { color: axisLabelColor },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text:
            view === "week"
              ? "Weeks of the Month"
              : view === "month"
                ? "Days of the Month"
                : view === "year"
                  ? "Months"
                  : "Years",
          color: axisLabelColor,
        },
        ticks: { color: axisLabelColor },
        grid: { color: gridColor },
      },
      y: {
        title: {
          display: true,
          text:
            view === "week" || view === "month"
              ? "Total Hours Worked"
              : "Total Days Worked",
          color: axisLabelColor,
        },
        beginAtZero: true,
        ticks: { stepSize: view === "month" || view === "week" ? 2 : 1, color: axisLabelColor },
        grid: { color: gridColor },
      },
    },
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* 🏷️ Total Summary */}
      {view === "week" || view === "month" ? (
        <Text fontSize="md" fontWeight="bold" align="center" color={textColor}>
          Total Days Worked: {filteredData.totalDaysWorked} | Total Hours
          Worked: {filteredData.totalHoursWorked}
        </Text>
      ) : (
        <Text fontSize="md" fontWeight="bold" align="center" color={textColor}>
          Total Days Worked: {filteredData.totalDaysWorked}
        </Text>
      )}

      <Divider />

      {/* 📊 View Controls */}
      <HStack justify="space-between" mb={2}>
        {/* View Buttons */}
        <HStack spacing={2}>
          {["week", "month", "year", "overall"].map((type) => (
            <Button
              key={type}
              size="sm"
              variant={view === type ? "gold" : "outline"}
              borderColor={goldBorder}
              color={view === type ? "white" : goldBorder}
              bg={view === type ? undefined : "transparent"}
              _hover={{
                bg: view === type ? undefined : outlineHoverBg,
                borderColor: goldBorder,
                color: view === type ? "white" : goldBorder,
              }}
              _active={{ bg: view === type ? undefined : outlineHoverBg }}
              onClick={() => setView(type as any)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
          ))}
        </HStack>

        {/* Year and Month Selectors */}
        <HStack spacing={4}>
          <HStack>
            <Text fontSize="sm">Year:</Text>
            <CustomDropdown
              value={String(selectedYear)}
              onChange={(value) => setSelectedYear(Number(value))}
              isDark={isDarkDropdown}
              options={Array.from({ length: 5 }, (_, i) => {
                const y = currentYear - 4 + i;
                return { value: String(y), label: String(y) };
              })}
              className="w-24 px-2 py-1 text-sm rounded-md border"
              style={{ height: "32px", minWidth: "88px" }}
            />
          </HStack>

          <HStack display={(view === "week" || view === "month") ? "flex" : "none"}>
            <Text fontSize="sm">Month:</Text>
            <CustomDropdown
              value={String(selectedMonth)}
              onChange={(value) => setSelectedMonth(Number(value))}
              isDark={isDarkDropdown}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: format(new Date(0, i), "MMMM") }))}
              className="w-36 px-2 py-1 text-sm rounded-md border"
              style={{ height: "32px", minWidth: "118px" }}
            />
          </HStack>
        </HStack>
      </HStack>

      {/* 📊 Graph Display */}
      <Box height="40vh">
        <Line data={chartData} options={chartOptions} />
      </Box>
    </VStack>
  );
};

export default TimesheetGraph;